import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import React, { useCallback, useMemo } from 'react';
import { useFlexLayout, useTable } from 'react-table';

import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex';
import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import { createActionsColumnDefinition } from './columns/actions';
import { createBoxPalletNumberColumnDefinition } from './columns/box-pallet-number';
import { createBoxesPerConfigColumnDefinition } from './columns/boxes-per-config';
import { createItemInformationColumnDefinition } from './columns/item-information';
import { createQuantityPerBoxColumnDefinition } from './columns/quantity-per-box';
import { createQuantityRemainingColumnDefinition } from './columns/quantity-remaining';
import { createTotalQuantityColumnDefinition } from './columns/total-quantity';
import styles from './define-shipments-table.module.less';
import { messages } from './messages';

const AddProductToBoxConfigurationButton = ({ row, onAddProductToBoxConfiguration }) => {
  const { isLastProduct, isDisabledAddSkusButton } = row?.original || {};

  if (!isLastProduct) {
    return null;
  }

  return (
    <FlexRow className={styles['add-row-button']}>
      <Button disabled={isDisabledAddSkusButton} onClick={onAddProductToBoxConfiguration(row)} theme='link'>
        <FlexRow>
          <FontAwesomeIcon icon={faPlus} />
          <Text textType={TextType.Caption} value={messages.addSkusButtonText} />
        </FlexRow>
      </Button>
    </FlexRow>
  );
};

const getRowClassName = (row) => {
  return row?.original?.bodyRowClassName;
};

export const DefineShipmentsTable = ({
  selectedProducts,
  onAddProductToBoxConfiguration,
  data,
  onDeleteProduct,
  onSelectProduct,
  onChangeQuantityPerBox,
  onChangeBoxesPerConfig,
  getOptionsForDropdown,
  getDisabledOptionsForDropdown,
  isReadOnly,
}) => {
  const columns = useMemo(
    () => [
      createBoxPalletNumberColumnDefinition(),
      createItemInformationColumnDefinition(),
      createQuantityPerBoxColumnDefinition(),
      createBoxesPerConfigColumnDefinition(),
      createTotalQuantityColumnDefinition(),
      createQuantityRemainingColumnDefinition(),
      createActionsColumnDefinition(),
    ],
    [],
  );

  const hiddenColumns = isReadOnly ? ['actions'] : [];

  const { getTableProps, getTableBodyProps, rows, prepareRow, headerGroups } = useTable(
    {
      columns,
      data,
      initialState: {
        hiddenColumns,
      },
      onAddProductToBoxConfiguration,
      selectedProducts,
      onDeleteProduct,
      onSelectProduct,
      onChangeQuantityPerBox,
      onChangeBoxesPerConfig,
      getOptionsForDropdown,
      getDisabledOptionsForDropdown,
      isReadOnly,
    },
    useFlexLayout,
  );

  const renderSubRow = useCallback(
    (row) => <AddProductToBoxConfigurationButton onAddProductToBoxConfiguration={onAddProductToBoxConfiguration} row={row} />,
    [onAddProductToBoxConfiguration],
  );

  if (isReadOnly) {
    return (
      <ReactTableWrapper
        className={styles.table}
        getBodyRowClassName={getRowClassName}
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        rows={rows}
      />
    );
  }

  return (
    <ReactTableWrapper
      className={styles.table}
      getBodyRowClassName={getRowClassName}
      getTableBodyProps={getTableBodyProps}
      getTableProps={getTableProps}
      headerGroups={headerGroups}
      prepareRow={prepareRow}
      renderSubRow={renderSubRow}
      rows={rows}
    />
  );
};
